import React from 'react';
import { Card, P, Heading2, Stack, Button, theme } from '@ovotech/element-v5';
import { History } from 'history';
import {
  FooterRow,
  HeaderRow,
  LinkStyled,
  MilesText,
  PStyled,
} from './ChargeAnytimeBolton.styled';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import {
  canCancelChargeAnytime,
  canReinstateChargeAnytime,
  canSwitchChargeAnytime,
  getChargeAnytimePlanCardDetails,
} from '../../utils/chargeAnytimePlans';
import {
  BoltonPlanStatus,
  ChargeAnytimeProvider,
  connectedVehicleIntegrationType,
  EV,
  getPlanNameFromBolton,
  noConnectedDeviceWithVolvoSignUp,
  useChargeAnytimeProducts,
} from '@monovo/ev-anytime';
import {
  ROUTE_ANYTIME,
  ROUTE_ANYTIME_ELIGIBILITY,
} from '@/src/constants/routes';
import { routes } from '@/src/pages/AnytimeEligibilityV2/ChargeAnytime';
import { hasSmartMeter as hasSmartMeterSelector } from '@/src/pages/Renewal/selectors';
import { CHARGE_ANYTIME_REINSTATE } from '@/src/constants/feature-flags';

export type ChargeAnytimeBoltonProps = {
  history: History;
};
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import {
  useAccountContext,
  useAnalytics,
  useCustomerContext,
} from '@ovotech/ui-tools';
import { fetchFn } from '@/src/pages/AnytimeEligibilityV2/services/chargeAnytimeAPI';
import { useSelector } from 'react-redux';
import { StyledProductHeading } from '../../Products.styled';
import { StyledAnytimeIcon } from '../ProductIconAndLabel/ProductIconAndLabel.styled';
import { useFeature } from '@/src/utils/optimizely';

const ChargeAnytimeBoltonContent = ({ history }: ChargeAnytimeBoltonProps) => {
  const { dispatch: analyticsDispatch } = useAnalytics();

  const {
    boltonPlan,
    signupIntegrationType,
    isLoading: isBoltonLoading,
    vehicles,
  } = useChargeAnytimeBoltonState();

  const [isReinstateEnabled] = useFeature(CHARGE_ANYTIME_REINSTATE);

  const {
    data: products,
    isLoading: isProductsLoading,
    isError,
  } = useChargeAnytimeProducts('all');

  if (isError) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  if (isProductsLoading || isBoltonLoading || !boltonPlan || !vehicles) {
    return null;
  }

  const chargeAnytimeDetails = getChargeAnytimePlanCardDetails(
    products || [],
    boltonPlan,
    vehicles,
    signupIntegrationType,
  );

  if (!chargeAnytimeDetails) return null;

  const title = chargeAnytimeDetails?.title;
  const miles = chargeAnytimeDetails?.miles;
  const body = chargeAnytimeDetails?.text;

  const canCancel = canCancelChargeAnytime(boltonPlan);
  const canReinstate = canReinstateChargeAnytime(boltonPlan);
  const canSwitch = canSwitchChargeAnytime(
    boltonPlan,
    vehicles,
    signupIntegrationType,
  );

  const goToSwitchFlow = () => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_CLICKED,
        properties: {
          currentAddOnName: getPlanNameFromBolton(boltonPlan.current, products),
          currentAddOnState: boltonPlan.current?.status || null,
          pathname: '/plan',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.YOUR_ADD_ONS,
        },
      });
    }

    history.push({
      pathname: `${ROUTE_ANYTIME_ELIGIBILITY.SWITCH}`,
    });
  };

  const onCancel = () => {
    const vehicleIntegrationType = connectedVehicleIntegrationType(vehicles);

    const isVolvo =
      vehicleIntegrationType === 'deviceManufacturer' ||
      noConnectedDeviceWithVolvoSignUp(
        vehicleIntegrationType,
        signupIntegrationType,
      );

    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_CANCEL_CLICKED,
        properties: {
          currentAddOnName: getPlanNameFromBolton(boltonPlan.current, products),
          currentAddOnState: boltonPlan?.current?.status || null,
          futureAddOnName: getPlanNameFromBolton(boltonPlan.future, products),
          futureAddOnState: boltonPlan?.future?.status || null,
          isVolvo,
          pathname: '/plan',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.YOUR_ADD_ONS,
        },
      });
    }

    history.push({
      pathname: `${ROUTE_ANYTIME}${routes.disconnect}`,
    });
  };

  const onReactivate = () => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_CLICKED,
        properties: {
          currentAddOnName: getPlanNameFromBolton(boltonPlan.current, products),
          currentAddOnState: boltonPlan.current?.status || null,
          pathname: '/plan',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.YOUR_ADD_ONS,
        },
      });
    }

    history.push({
      pathname: ROUTE_ANYTIME_ELIGIBILITY.REACTIVATE,
    });
  };

  return (
    <Stack spaceBetween={5}>
      <StyledProductHeading as={Heading2} data-testid="products-bolton-title">
        <StyledAnytimeIcon aria-hidden="true" /> Charge Anytime
      </StyledProductHeading>
      <Card
        style={{
          padding: `18px ${theme.space['500']} ${theme.space['400']} ${theme.space['500']}`,
          borderRadius: 0,
        }}
      >
        <Stack spaceBetween={4}>
          <HeaderRow>
            <PStyled>{title}</PStyled>
            <MilesText>{miles}</MilesText>
          </HeaderRow>
          <P>{body}</P>
          <FooterRow>
            {canCancel ? (
              <LinkStyled onClick={onCancel}>
                {boltonPlan.status === BoltonPlanStatus.Switching
                  ? 'Cancel switch'
                  : 'Cancel'}
              </LinkStyled>
            ) : (
              <div /> // Spacer
            )}
            <Stack orientation="horizontal" spaceBetween={3}>
              {canReinstate && isReinstateEnabled && (
                <Button onClick={onReactivate}>Reactivate</Button>
              )}
              {canSwitch && (
                <Button onClick={goToSwitchFlow} iconRight="chevron-right">
                  Switch
                </Button>
              )}
            </Stack>
          </FooterRow>
        </Stack>
      </Card>
    </Stack>
  );
};

export const ChargeAnytimeBolton = ({ history }: ChargeAnytimeBoltonProps) => {
  const hasSmartMeter = useSelector(hasSmartMeterSelector) as boolean;

  const { accountId } = useAccountContext();
  const { customerId } = useCustomerContext();

  if (!accountId || !customerId) {
    return null;
  }

  const chargeAnytimeApi = { fetch: fetchFn };

  return (
    <ChargeAnytimeProvider
      chargeAnytimeApi={chargeAnytimeApi}
      customerId={customerId}
      accountId={accountId}
      hasSmartMeter={hasSmartMeter}
    >
      <ChargeAnytimeBoltonContent history={history} />
    </ChargeAnytimeProvider>
  );
};
